import { useCallback, useState } from 'react';
import useApi from '../api';
import { McpAuthMode, McpServer, McpServerTool, errorDetail } from './mcpServerRegistry';

export interface McpServerDraft {
  name: string;
  description: string;
  url: string;
  auth_mode: McpAuthMode;
  api_key: string;
}

export function draftFromServer(server?: McpServer | null): McpServerDraft {
  return {
    name: server?.name ?? '',
    description: server?.description ?? '',
    url: server?.url ?? '',
    auth_mode: server?.auth_mode ?? 'none',
    api_key: '',
  };
}

/**
 * Create/edit state for an org-level MCP server, shared by the Integrations
 * dialog and the onboarding "Connect your product" step. Owns the draft, the
 * test-connection call (`POST /mcp-connections/discover`) and the save
 * (`POST /mcp-servers` or `PUT /mcp-servers/{id}`). The credential is
 * write-only: on edit the field starts blank and only a non-empty value
 * replaces the stored key.
 */
export function useMcpServerDraft(server?: McpServer | null) {
  const { post, put } = useApi();
  const isEdit = Boolean(server);

  const [draft, setDraft] = useState<McpServerDraft>(() => draftFromServer(server));
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ tools: McpServerTool[] } | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [startingOAuth, setStartingOAuth] = useState(false);

  const reset = useCallback((next?: McpServer | null) => {
    setDraft(draftFromServer(next));
    setTestResult(null);
    setTestError(null);
    setSaveError(null);
  }, []);

  const setField = useCallback((patch: Partial<McpServerDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setSaveError(null);
  }, []);

  /** Fields that change which server we talk to invalidate the last test. */
  const updateConnectionDetails = useCallback((patch: Partial<McpServerDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setTestResult(null);
    setTestError(null);
    setSaveError(null);
  }, []);

  const connectionChanged =
    !isEdit ||
    draft.url.trim() !== server?.url ||
    draft.auth_mode !== server?.auth_mode ||
    Boolean(draft.api_key.trim());

  const keyRequired =
    draft.auth_mode === 'api_key' && (!isEdit || !server?.has_credential || server.auth_mode !== 'api_key');

  /**
   * OAuth sign-in: `POST /mcp-servers/oauth/start` mints the authorize URL and
   * the browser leaves for the product's login. The backend callback creates
   * or updates the server row (discovery included) and sends the browser back
   * with `?mcp_oauth=connected&mcp_server_id=...` (or `mcp_oauth=error`).
   * Returns false when the request failed (error shown via saveError).
   */
  const startOAuth = useCallback(
    async (returnTo: 'integrations' | 'onboarding'): Promise<boolean> => {
      if (!draft.url.trim()) {
        setSaveError('Server URL is required.');
        return false;
      }
      if (!draft.name.trim()) {
        setSaveError('Name is required.');
        return false;
      }
      setStartingOAuth(true);
      setSaveError(null);
      try {
        const response = await post('/mcp-servers/oauth/start', {
          url: draft.url.trim(),
          name: draft.name.trim(),
          ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
          ...(server?.id ? { server_id: server.id } : {}),
          return_to: returnTo,
        });
        const authorizeUrl = response.data?.authorize_url as string | undefined;
        if (!authorizeUrl) throw new Error('No authorize URL returned.');
        window.location.assign(authorizeUrl);
        return true;
      } catch (error: any) {
        setSaveError(errorDetail(error, 'Could not start sign-in for this MCP server.'));
        setStartingOAuth(false);
        return false;
      }
    },
    [draft, post, server]
  );

  const test = useCallback(async () => {
    if (draft.auth_mode === 'oauth') {
      if (!isEdit || !server?.id || server.auth_mode !== 'oauth') {
        setTestError('Sign in first. Testing uses the token stored by sign-in.');
        return null;
      }
      setTesting(true);
      setTestError(null);
      try {
        const response = await post('/mcp-connections/discover', { server_id: server.id });
        const result = { tools: (response.data?.tools ?? []) as McpServerTool[] };
        setTestResult(result);
        return result;
      } catch (error: any) {
        setTestError(errorDetail(error, 'Could not connect to MCP server.'));
        return null;
      } finally {
        setTesting(false);
      }
    }
    if (!draft.url.trim()) {
      setTestError('Enter a server URL first.');
      return null;
    }
    if (draft.auth_mode === 'api_key' && !draft.api_key.trim()) {
      setTestError(
        isEdit && server?.has_credential
          ? 'Enter the API key to test. The stored key is never sent back to the browser; saving without a new key keeps the stored one.'
          : 'Enter an API key first.'
      );
      return null;
    }
    setTesting(true);
    setTestError(null);
    try {
      const response = await post('/mcp-connections/discover', {
        url: draft.url.trim(),
        auth_mode: draft.auth_mode,
        ...(draft.auth_mode === 'api_key' ? { api_key: draft.api_key } : {}),
      });
      const result = { tools: (response.data?.tools ?? []) as McpServerTool[] };
      setTestResult(result);
      return result;
    } catch (error: any) {
      setTestError(errorDetail(error, 'Could not connect to MCP server.'));
      return null;
    } finally {
      setTesting(false);
    }
  }, [draft, isEdit, post, server]);

  const save = useCallback(async (): Promise<McpServer | null> => {
    if (!draft.name.trim()) {
      setSaveError('Name is required.');
      return null;
    }
    if (!draft.url.trim()) {
      setSaveError('Server URL is required.');
      return null;
    }
    if (keyRequired && !draft.api_key.trim()) {
      setSaveError('An API key is required for API key authentication.');
      return null;
    }
    if (draft.auth_mode === 'oauth' && (!isEdit || server?.auth_mode !== 'oauth')) {
      // Switching to (or creating with) sign-in always goes through the
      // authorize round-trip; the callback saves the row. Callers that need
      // the redirect target should call startOAuth directly.
      setSaveError('Use "Sign in" to connect this server.');
      return null;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const body: Record<string, any> = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        url: draft.url.trim(),
        auth_mode: draft.auth_mode,
      };
      if (draft.auth_mode === 'api_key' && draft.api_key.trim()) {
        body.api_key = draft.api_key.trim();
      }
      const response = isEdit
        ? await put(`/mcp-servers/${server!.id}`, body)
        : await post('/mcp-servers', body);
      return response.data as McpServer;
    } catch (error: any) {
      setSaveError(errorDetail(error, isEdit ? 'Could not save MCP server.' : 'Could not connect MCP server.'));
      return null;
    } finally {
      setSaving(false);
    }
  }, [draft, isEdit, keyRequired, post, put, server]);

  return {
    isEdit,
    draft,
    setField,
    updateConnectionDetails,
    reset,
    connectionChanged,
    keyRequired,
    test,
    testing,
    testResult,
    testError,
    save,
    saving,
    saveError,
    startOAuth,
    startingOAuth,
  };
}
